import {
  ActivePracticeContext,
  PracticesContext,
} from "../../contexts/contextExporter";
import { useContext, useState, useEffect } from "react";

import {
  IPracticeHistoryItem,
} from "../../types/generalTypes";
import { prepareUiHistory } from "../../utils/common";
import TestPractice from "./TestPractice";

import CodePracticePreSandpack from "./CodePracticePreSandpack";
import CodePracticeBasic from "./CodePracticeBasic";
import CodePracticePython from "./CodePracticePython";
import CodePracticeSandpack from "./CodePracticeSandpack";
import InstructorReveal from "../../components/InstructorReveal";
import CodeShiki from "../../components/CodeShiki";

export default function PracticeItemMain({
  initialHistory,
  isLive,
}: {
  initialHistory: IPracticeHistoryItem[];
  isLive: boolean;
}) {
  const [aiReply, setAiReply] = useState<string | null>(null);
  const [userAnswer, setUserAnswer] = useState<string | null>(null);

  const [history, setHistory] =
    useState<IPracticeHistoryItem[]>(initialHistory);

  const {
    practice,
    actionStatus,
    aiDialogHistory,
    isInstructor,
  } = useContext(ActivePracticeContext);
  const { isLastPractice } = useContext(PracticesContext);

  // REST response geldiğinde aiDialogHistory güncellenir, history'yi oradan türet
  useEffect(() => {
    const practiceHistory = aiDialogHistory[practice?.id];
    if (practiceHistory?.length) {
      setHistory(prepareUiHistory(practiceHistory));
      // Son AI cevabını aiReply'a set et
      const lastAssistant = [...practiceHistory].reverse().find(h => h.role === "assistant");
      if (lastAssistant) {
        setAiReply(lastAssistant.content);
      }
    }
  }, [aiDialogHistory, practice?.id]);

  const isPastItem = actionStatus.historyIndex > -1;
  const currentHistoryItem = history[actionStatus.historyIndex];

  const currentAiContent = !isPastItem
    ? actionStatus.ai === "LOADING"
      ? null
      : aiReply
    : (currentHistoryItem?.answer ?? aiReply);

  if (!practice) return null;

  if (
    isLastPractice &&
    actionStatus.canGoNext &&
    actionStatus.ai === "IDLE" &&
    isLive
  ) {
    return (
      <div className="bg-surface-1 border border-white/[0.08] rounded-2xl max-w-md p-10 mx-auto w-full mt-12 text-center">
        <div className="text-2xl text-myGreen font-semibold">Tebrikler!</div>
        <div className="text-sm text-white/40 mt-3 leading-relaxed">
          Bu bölümdeki tüm alıştırmaları bitirdin. Diğer bölüme geçene kadar
          lütfen bekle.
        </div>
      </div>
    );
  }
  if (practice.type === "TEST") {
    return (
      <div className="max-w-3xl overflow-auto mx-auto p-4 md:p-6 pt-8 md:pt-10 h-full">
        <TestPractice
          currentAiContent={currentAiContent}
          setAiReply={setAiReply}
          setUserAnswer={setUserAnswer}
          history={history}
          isLive={isLive}
        />
        {isInstructor && (
          <InstructorReveal>{practice.description.answer}</InstructorReveal>
        )}
      </div>
    );
  }
  if (practice.type === "CODE" && practice.tags === "preSandpack") {
    return (
      <div className="w-full h-full overflow-auto px-4 md:px-6 pt-6 md:pt-8 pb-12">
        <CodePracticePreSandpack
          currentAiContent={currentAiContent}
          setAiReply={setAiReply}
          setUserAnswer={setUserAnswer}
          history={history}
          isLive={isLive}
        />
        {isInstructor && (
          <InstructorReveal>
            <CodeShiki code={practice.description.answer} cx="text-sm" />
          </InstructorReveal>
        )}
      </div>
    );
  }

  if (practice.type === "REACT") {
    const { answerFiles } = practice.description;
    return (
      <div className="w-full h-full overflow-auto px-4 md:px-6 pt-6 md:pt-8 pb-12">
        <CodePracticeSandpack
          currentAiContent={currentAiContent}
          setAiReply={setAiReply}
          setUserAnswer={setUserAnswer}
          history={history}
          isLive={isLive}
        />
        {answerFiles && isInstructor && (
          <InstructorReveal>
            <div className="space-y-4">
              {Object.keys(answerFiles).map((s) => (
                <div key={s}>
                  <p className="mb-1">{s}</p>
                  <CodeShiki
                    code={
                      typeof answerFiles[s] === "string"
                        ? answerFiles[s]
                        : answerFiles[s].code
                    }
                    cx="text-sm"
                  />
                </div>
              ))}
            </div>
          </InstructorReveal>
        )}
      </div>
    );
  }

  if (practice.type === "PYTHON") {
    return (
      <div className="w-full h-full overflow-auto px-4 md:px-6 pt-6 md:pt-8 pb-12">
        <CodePracticePython
          currentAiContent={currentAiContent}
          setAiReply={setAiReply}
          setUserAnswer={setUserAnswer}
          history={history}
          isLive={isLive}
        />
        {isInstructor && (
          <InstructorReveal>
            <CodeShiki code={practice.description.answer} cx="text-sm" />
          </InstructorReveal>
        )}
      </div>
    );
  }

  if (practice.type === "CODE" && !practice.tags) {
    return (
      <div className="w-full h-full overflow-auto px-4 md:px-6 pt-6 md:pt-8 pb-12">
        <CodePracticeBasic
          currentAiContent={currentAiContent}
          setAiReply={setAiReply}
          setUserAnswer={setUserAnswer}
          history={history}
          isLive={isLive}
        />
        {isInstructor && (
          <InstructorReveal>
            <CodeShiki code={practice.description.answer} cx="text-sm" />
          </InstructorReveal>
        )}
      </div>
    );
  }
}
